import { useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { PageHeader, SkeletonRows, todayLabel } from '../../components/primitives';
import { useIsMobile } from '../../lib/useIsMobile';
import { AUTOMATION_META } from './constants';
import type { AutomationsPayload, HubAutomationKey, TemplateRow, TemplatesState } from './types';
import { AutomationCard } from './AutomationCard';
import { TemplateEditorFullCanvas } from './TemplateEditorFullCanvas';
import { TestimonialPrompts } from './TestimonialPrompts';

// Automations hub — Handoff C (docs/HANDOFF-automations-hub-2026-07.md),
// mockup docs/mockups-automations-hub-2026-07.html approved 2026-07 with one
// change: the sidebar slot moved to directly above Settings (wired in
// Shell.tsx), not under Overview as the mockup's Option A drew it.
//
// One card per automations.ts INVENTORY row: plain-language description,
// on/off toggle (or a greyed "always on" badge for the one manual action),
// last run/fired + outcome, editable knobs, inline error visibility, a
// send-history drill-down, and a template editor modal. The template
// endpoints (GET/PUT/DELETE/preview under .../automations/templates) are a
// concurrent Phase 2 build — every path here degrades gracefully if they
// 404, per the handoff.

export default function Automations() {
  const [data, setData] = useState<AutomationsPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [templatesState, setTemplatesState] = useState<TemplatesState>({ status: 'loading' });
  const [editing, setEditing] = useState<{ key: HubAutomationKey; title: string; templates: TemplateRow[] } | null>(
    null,
  );
  const isMobile = useIsMobile();

  const load = useCallback(() => {
    return api<AutomationsPayload>('/api/admin/automations')
      .then((d) => {
        // The server can ship a new automation before its card exists here;
        // skip keys without presentation metadata instead of crashing the page.
        setData({ ...d, automations: d.automations.filter((a) => a.key in AUTOMATION_META) });
        setError(null);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load automations.'));
  }, []);

  const loadTemplates = useCallback(() => {
    api<{ templates: TemplateRow[] }>('/api/admin/automations/templates')
      .then((d) => {
        // Route not mounted yet (Phase 2, built concurrently) falls through
        // to the SPA catch-all, which resolves 200 with the app shell, not
        // JSON — treat anything without a real array as "not shipped yet"
        // rather than crashing on d.templates being undefined.
        if (!Array.isArray(d.templates)) throw new Error('templates unavailable');
        setTemplatesState({ status: 'ready', templates: d.templates });
      })
      .catch(() => setTemplatesState({ status: 'unavailable' }));
  }, []);

  useEffect(() => {
    load();
    loadTemplates();
  }, [load, loadTemplates]);

  const toggleEnabled = (key: HubAutomationKey, enabled: boolean) => {
    setBusyKey(key);
    api(`/api/admin/automations/${key}`, { method: 'PATCH', json: { enabled } })
      .then(load)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to update automation.'))
      .finally(() => setBusyKey(null));
  };

  const toggleEmailPaused = (paused: boolean) => {
    setBusyKey('__email_paused__');
    api('/api/admin/automations/email-paused', { method: 'PUT', json: { paused } })
      .then(load)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to update email sending.'))
      .finally(() => setBusyKey(null));
  };

  const saveKnob = (key: HubAutomationKey, knobKey: string, value: number | string) => {
    setBusyKey(key);
    return api(`/api/admin/automations/${key}`, { method: 'PATCH', json: { knobs: { [knobKey]: value } } })
      .then(() => load())
      .catch((e) => {
        // Text knobs show their own validation message under the field.
        if (typeof value !== 'string') setError(e instanceof Error ? e.message : 'Failed to update automation.');
        throw e;
      })
      .finally(() => setBusyKey(null));
  };

  const toggleExpanded = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const templateCounts = new Map<string, number>();
  if (templatesState.status === 'ready') {
    for (const t of templatesState.templates) {
      templateCounts.set(t.automation, (templateCounts.get(t.automation) ?? 0) + 1);
    }
  }

  const attentionCount = data?.automations.filter((a) => a.recent_errors.length > 0).length ?? 0;
  const attentionLabel =
    attentionCount > 0
      ? attentionCount === 1
        ? '1 needs attention'
        : `${attentionCount} need attention`
      : 'All clear';

  return (
    <>
      {/* ≤880px takes the shared PageHeader so the mobile header treatment
          (28px title, mono sync strip) applies. Desktop keeps this page's own
          header verbatim — PageHeader has no error state for the live dot, so
          swapping it there would change the rendered markup. */}
      {isMobile ? (
        <PageHeader
          title="Automations"
          count={data ? data.automations.length : undefined}
          liveLabel={attentionLabel}
        />
      ) : (
        <header className="page-header">
          <span className="page-title">
            Automations
            {data && <span className="page-title-count"> {data.automations.length}</span>}
          </span>
          <div className="header-right">
            <span className="live">
              <span className={`live-dot${attentionCount > 0 ? ' err' : ''}`} />
              {attentionLabel}
            </span>
            <span>{todayLabel()}</span>
          </div>
        </header>
      )}

      <div className="page-body">
        <div className="content">
          {error && (
            <p className="error-text" style={{ marginBottom: 12 }}>
              {error}
            </p>
          )}
          {data === null ? (
            <SkeletonRows rows={6} />
          ) : (
            <>
              <p className="atm-summary">
                All {data.automations.length} run inside the nightly <b>03:00</b> cron, each in its own{' '}
                <b>try/catch</b> — one erroring never blocks the rest.
              </p>
              {templatesState.status === 'unavailable' && (
                <p className="empty-note" style={{ marginTop: -10, marginBottom: 18 }}>
                  Template editing isn't live yet — check back once it ships.
                </p>
              )}
              <div className={`atm-card atm-email-master${data.email_paused ? ' email-paused' : ''}`}>
                <div className="atm-head">
                  <div className="atm-title-block">
                    <div className="atm-title">
                      Email sending
                      {data.email_paused && <span className="atm-paused-badge">ALL EMAIL PAUSED</span>}
                    </div>
                  </div>
                  <button
                    type="button"
                    className={`${isMobile ? 'toggle' : 'atm-switch'}${data.email_paused ? '' : ' on'}`}
                    role="switch"
                    aria-checked={!data.email_paused}
                    aria-label={data.email_paused ? 'Resume all email sending' : 'Pause all email sending'}
                    disabled={busyKey === '__email_paused__'}
                    onClick={() => toggleEmailPaused(!data.email_paused)}
                  />
                </div>
                <p className="atm-desc">
                  Master switch — pauses every outgoing email, automations and manual sends alike.
                </p>
                {data.email_paused && (
                  <p className="atm-paused-note">
                    One-off receipts (e.g. proposal signed) that occur while paused are skipped, not queued. Nightly
                    automations resume on the next run.
                  </p>
                )}
              </div>
              <div className="atm-list">
                {data.automations.map((item) => (
                  <AutomationCard
                    key={item.key}
                    item={item}
                    isMobile={isMobile}
                    emailPaused={data.email_paused}
                    busy={busyKey === item.key}
                    expanded={expanded.has(item.key)}
                    templateCount={templateCounts.get(item.key) ?? 0}
                    onToggleExpanded={() => toggleExpanded(item.key)}
                    onToggleEnabled={(enabled) => toggleEnabled(item.key, enabled)}
                    onSaveKnob={(k, v) => saveKnob(item.key, k, v)}
                    onEditTemplate={() => {
                      if (templatesState.status !== 'ready') return;
                      setEditing({
                        key: item.key,
                        title: AUTOMATION_META[item.key].title,
                        templates: templatesState.templates.filter((t) => t.automation === item.key),
                      });
                    }}
                  />
                ))}
              </div>
              <TestimonialPrompts />
            </>
          )}
        </div>
      </div>

      {editing && (
        <TemplateEditorFullCanvas
          automationTitle={editing.title}
          initialTemplates={editing.templates}
          onSaved={loadTemplates}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}
